/**
 * EQ Exporter (migrated to feature folder)
 *
 * This file is a one-to-one migration of the original
 * `vite-app/src/export/eq-exporter.ts` with updated relative imports
 * to the new feature-based layout.
 *
 * Purpose: keep behavior unchanged while allowing the export feature
 * to live under `src/features/export-manager/`.
 */

import { EQSetting, ExportOptions, EQProcessorInterface, EQExporterInterface, TestResults } from '../../types/index';
import { interpAt } from '../../core/math-utils';
import { buildParametricFilters, calculateParametricPreamp } from '../../core/parametric-eq';
import { presetFrequencies as defaultPresetFrequencies } from '../preset-manager/presets';

class EQExporter implements EQExporterInterface {
    eqProcessor: EQProcessorInterface | null;
    isNodeEnv: boolean;

    constructor(eqProcessor: EQProcessorInterface | null) {
        this.eqProcessor = eqProcessor;
        this.isNodeEnv = typeof window === 'undefined';
    }

    /**
     * Export EQ configuration in specified format
     * @param {Array} eqSettings - EQ settings to export
     * @param {string} format - 'parametric' or 'graphic'
     * @param {Object} options - Export options
     * @returns {string} Export content
     */
    exportConfiguration(eqSettings: EQSetting[], format: 'parametric' | 'graphic', options: ExportOptions = {}): string {
        if (format === 'parametric') {
            return this.exportParametric(eqSettings, options);
        } else if (format === 'graphic') {
            return this.exportGraphic(eqSettings, options);
        }
        throw new Error(`Unknown export format: ${format}`);
    }

    /**
     * Export as parametric EQ format
     * @param {Array} eqSettings - EQ settings
     * @param {Object} options - Export options
     * @returns {string} Parametric EQ text
     */
    exportParametric(eqSettings: EQSetting[], options: ExportOptions = {}): string {
        const presetFrequencies = options.presetFrequencies || defaultPresetFrequencies;
        const filters = (eqSettings && eqSettings.length > 0)
            ? buildParametricFilters(eqSettings, { presetFrequencies, peakQuality: options.peakQuality ?? 0 })
            : [];

        // The preamp comes from the simulated response of exactly these filters, because
        // overlapping PK boosts add up above the highest band gain. It is a web-app
        // clipping-prevention extra — Peace generates no Preamp line for hearing-test configs
        // (ResultToEqualization, Peace16911.au3:6320) — so there is no parity value to match.
        const preamp = calculateParametricPreamp(filters, options.headroom ?? 0.2);
        const preampText = preamp >= 0 ? `+${preamp.toFixed(1)}` : preamp.toFixed(1);
        let content = `Preamp: ${preampText} dB\n`;

        filters.forEach(({ frequency, gain, q }, index) => {
            content += `Filter ${index + 1}: ON PK Fc ${frequency} Hz Gain ${gain.toFixed(2)} dB Q ${q.toFixed(2)}\n`;
        });

        return content;
    }

    /**
     * Export as graphic EQ format (textual, AutoEQ-style, importable by Wavelet).
     * The format has no separate preamp line, so the curve is shifted down so its
     * highest point sits at -headroom dB (AutoEQ convention) to prevent clipping.
     * @param {Array} eqSettings - EQ settings
     * @param {Object} options - Export options
     * @returns {string} Graphic EQ text
     */
    exportGraphic(eqSettings: EQSetting[], options: ExportOptions = {}): string {
        const presetFrequencies = options.presetFrequencies || defaultPresetFrequencies;
        let content = 'GraphicEQ: ';

        if (!eqSettings || eqSettings.length === 0) {
            return content;
        }

        // Build sorted settings for interpolation using canonical helpers
        const sortedSettings2 = eqSettings.slice().sort((a: EQSetting, b: EQSetting) => a.frequency - b.frequency);

        // Iterate through ALL preset frequencies and interpolate missing values
        const points: Array<[number, number]> = [];
        presetFrequencies.forEach((freq: number) => {
            let gain = 0;
            if (sortedSettings2.length > 0) {
                const exact = sortedSettings2.find(s => Math.abs(s.frequency - freq) < 1);
                if (exact) {
                    gain = exact.gain || 0;
                } else {
                    let lower: EQSetting | null = null;
                    let upper: EQSetting | null = null;
                    for (let i = 0; i < sortedSettings2.length; i++) {
                        if (sortedSettings2[i].frequency <= freq) lower = sortedSettings2[i];
                        if (sortedSettings2[i].frequency >= freq && !upper) upper = sortedSettings2[i];
                    }
                    if (lower && upper) {
                        if (lower.frequency === upper.frequency) gain = lower.gain || 0;
                        else gain = interpAt(freq, [lower.frequency, lower.gain] as any, [upper.frequency, upper.gain] as any, 'log', 'log');
                    } else if (lower) {
                        gain = lower.gain || 0;
                    } else if (upper) {
                        gain = upper.gain || 0;
                    }
                }
            }
            points.push([freq, gain]);
        });

        const headroom = options.headroom ?? 0.2;
        const shift = -(Math.max(...points.map(([, gain]) => gain)) + headroom);
        content += points.map(([freq, gain]) => `${freq} ${(gain + shift).toFixed(1)}`).join('; ');
        return content;
    }

    /**
     * Build export payload for programmatic use
     * @param {Object} testResults - Test results
     * @param {string} format - Export format
     * @returns {Object} Export payload
     */
    buildExportPayload(testResults: TestResults, format: 'parametric' | 'graphic'): { format: string; timestamp: string; testResults: TestResults; generatedBy: string } {
        return {
            format: format,
            timestamp: new Date().toISOString(),
            testResults: testResults,
            generatedBy: 'Peace EQ Web Application'
        };
    }

    /**
     * Download content as file (browser only)
     * @param {string} content - File content
     * @param {string} filename - File name
     */
    downloadAsFile(content: string, filename: string): void {
        if (this.isNodeEnv) {
            // For Node.js, just return the content
            return;
        }

        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
}

// ES module export
export { EQExporter };
export default EQExporter;